import React, { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { User } from '../../types';
import { resolveTester } from '../../utils/tester';
import { INTERNAL_PROGRAM } from '../../config/deployProgram';
import { loginPathForPortal, safePortalNext } from '../../utils/portalNext';
import TestingLandingPage from './TestingLandingPage';
import BetaAuthPage from './BetaAuthPage';
import PublicDeployTestPage from './PublicDeployTestPage';
import TestingHomeView from './TestingHomeView';
import BetaTakeTestPage from './BetaTakeTestPage';

interface TestingPortalViewProps {
  user: User | null;
  onAuth: (user: User) => void;
  onLogout: () => void;
}

const LEFT_TESTING_FLAG = 'fluora-testing-just-left';
const LEFT_BANNER_MS = 10000;
const PORTAL_CLASS = 'beta-portal beta-portal-internal';

const TestingPortalView: React.FC<TestingPortalViewProps> = ({ user, onAuth, onLogout }) => {
  const location = useLocation();
  const [leftBanner, setLeftBanner] = useState(() => {
    try {
      return sessionStorage.getItem(LEFT_TESTING_FLAG) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!leftBanner) return;
    const timer = window.setTimeout(() => {
      setLeftBanner(false);
      try {
        sessionStorage.removeItem(LEFT_TESTING_FLAG);
      } catch {
        // Banner cleanup is optional.
      }
    }, LEFT_BANNER_MS);
    return () => window.clearTimeout(timer);
  }, [leftBanner]);

  const path = location.pathname.replace(/\/+$/, '') || '/';
  const isPublicTestPage = path === '/tests' || path.startsWith('/tests/');
  const isTakePage = path.startsWith('/take/');

  const header = (
    <header className="beta-portal-topbar">
      <Link to="/" className="beta-portal-brand">
        <img src="/logo-mark.png" alt="" className="beta-portal-mark" />
        <div>
          <div className="beta-portal-kicker">Internal Testing</div>
          {user && (
            <div className="beta-portal-user">
              {user.firstName} {user.lastName}
            </div>
          )}
        </div>
      </Link>
      {user ? (
        <button type="button" className="btn btn-outline-light btn-sm" onClick={onLogout}>
          Sign out
        </button>
      ) : (
        <Link to="/login" className="btn btn-outline-light btn-sm">
          Log in
        </Link>
      )}
    </header>
  );

  if (isPublicTestPage) {
    return (
      <div className={PORTAL_CLASS}>
        {header}
        <Routes>
          <Route path="/tests/:name" element={<PublicDeployTestPage />} />
        </Routes>
      </div>
    );
  }

  if (user) {
    if (path === '/login' || path === '/register') {
      return <Navigate to={safePortalNext(location.search)} replace />;
    }

    const tester = resolveTester(user);
    const canTakeTests = tester === 'internal';

    if (isTakePage) {
      if (!canTakeTests) {
        return <Navigate to="/" replace />;
      }
      return (
        <div className={PORTAL_CLASS}>
          {header}
          <Routes>
            <Route path="/take/:testId" element={<BetaTakeTestPage program={INTERNAL_PROGRAM} />} />
          </Routes>
        </div>
      );
    }

    return (
      <div className={PORTAL_CLASS}>
        {header}
        <main className="beta-portal-main">
          <TestingHomeView user={user} />
        </main>
      </div>
    );
  }

  return (
    <div className={PORTAL_CLASS}>
      {header}
      {leftBanner && (
        <div className="beta-left-banner" role="status">
          <p>You have successfully left Internal Testing</p>
          <button
            type="button"
            className="beta-left-banner-close"
            onClick={() => setLeftBanner(false)}
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
      <Routes>
        <Route path="/" element={<TestingLandingPage />} />
        <Route
          path="/login"
          element={(
            <BetaAuthPage
              mode="login"
              onAuth={onAuth}
              allowRegister={false}
              lead="Sign in with an account that has already been granted internal testing access."
            />
          )}
        />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/tests/:name" element={<PublicDeployTestPage />} />
        <Route path="/take/:testId" element={<Navigate to={loginPathForPortal(path)} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

export default TestingPortalView;
